import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getApiBaseUrl } from '../../api/client';
import EventFallbackPattern from '../EventFallbackPattern';
import './SectionFilter.css';

export interface SectionFilterItem {
  _id: number;
  name: string;
  description?: string;
  activityIds: number[];
}

export interface SectionFilterActivity {
  _id: number | string;
  name?: string;
  coverImage?: string | null;
}

interface SectionFilterProps {
  sections: SectionFilterItem[];
  activities: SectionFilterActivity[];
  /** Single active section, or null for "show all". */
  selectedId: number | null;
  onChange: (id: number | null) => void;
}

/** Resolve a stored cover path to a full URL (relative /uploads → API base). */
const resolveCoverUrl = (path?: string | null): string | null => {
  if (!path) return null;
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  if (path.startsWith('/uploads')) return `${getApiBaseUrl()}${path}`;
  return path;
};

const SectionFilter: React.FC<SectionFilterProps> = ({
  sections,
  activities,
  selectedId,
  onChange,
}) => {
  const { t } = useTranslation();

  // Map of activity id → resolved cover URL (only for activities with a cover).
  const coverById = useMemo(() => {
    const map = new Map<number, string>();
    for (const a of activities) {
      const url = resolveCoverUrl(a.coverImage);
      if (url) map.set(Number(a._id), url);
    }
    return map;
  }, [activities]);

  if (!sections || sections.length === 0) return null;

  // Single-select: click a section to filter; click the active one to clear.
  const select = (id: number) => {
    onChange(selectedId === id ? null : id);
  };

  return (
    <div className="section-filter-block">
      <h3 className="section-filter-block__title">{t('sections.title')}</h3>
      <div className="section-filter" role="group" aria-label={t('sections.title')}>
        {sections.map((section) => {
        const isActive = selectedId === section._id;
        // First associated activity that has a cover image.
        const coverUrl = section.activityIds
          .map((aid) => coverById.get(Number(aid)))
          .find((url): url is string => Boolean(url));

        return (
          <button
            key={section._id}
            type="button"
            className={`section-filter__card${isActive ? ' is-active' : ''}`}
            onClick={() => select(section._id)}
            aria-pressed={isActive}
          >
            <span className="section-filter__media">
              {coverUrl ? (
                <img src={coverUrl} alt="" loading="lazy" />
              ) : (
                <EventFallbackPattern seed={String(section._id)} />
              )}
            </span>
            <span className="section-filter__info">
              <span className="section-filter__title">{section.name}</span>
              {section.description && (
                <span className="section-filter__desc">{section.description}</span>
              )}
            </span>
          </button>
        );
        })}
      </div>
    </div>
  );
};

export default SectionFilter;
